import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const AdminReviewPanel = ({ user, addNotification }) => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [rejectedReviews, setRejectedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const getToken = () => localStorage.getItem('nlngra_jwt');

  const fetchPendingReviews = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const response = await fetch('https://nlngraclub.com/nlnapi/get_pending_associates.php', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setPendingReviews(data.pending || []);
        setApprovedReviews(data.approved || []);
        setRejectedReviews(data.rejected || []);
      } else {
        setMessage(data.message || 'Failed to load pending reviews');
        setIsError(true);
      }
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
      setMessage('Network error. Please try again.');
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingReviews();
  }, []);

  const handleApprove = async (application) => {
    setProcessingId(application.id);
    setMessage('');
    setIsError(false);

    try {
      const token = getToken();
      const response = await fetch('https://nlngraclub.com/nlnapi/approve_associate.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_id: application.id,
          notes: `Approved by admin on ${new Date().toLocaleString()}`
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`${application.first_name} ${application.last_name} has been APPROVED successfully!`);
        setShowSuccessModal(true);
        if (addNotification) addNotification(`${application.first_name} ${application.last_name} has been approved.`, 'success');
        fetchPendingReviews();
        setTimeout(() => setShowSuccessModal(false), 3000);
      } else {
        setMessage(data.message || 'Failed to approve application');
        setIsError(true);
        if (addNotification) addNotification(data.message || 'Failed to approve application', 'error');
      }
    } catch (error) {
      console.error('Error approving application:', error);
      setMessage('Network error. Please try again.');
      setIsError(true);
      if (addNotification) addNotification('Network error. Please try again.', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedApplication) return;
    if (!rejectionReason.trim()) {
      setMessage('Please provide a reason for rejection');
      setIsError(true);
      if (addNotification) addNotification('Please provide a reason for rejection', 'warning');
      return;
    }

    setProcessingId(selectedApplication.id);
    setMessage('');
    setIsError(false);

    try {
      const token = getToken();
      const response = await fetch('https://nlngraclub.com/nlnapi/reject_associate.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          member_id: selectedApplication.id,
          reason: rejectionReason
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`${selectedApplication.first_name} ${selectedApplication.last_name} has been REJECTED. Reason: ${rejectionReason}`);
        setShowSuccessModal(true);
        if (addNotification) addNotification(`${selectedApplication.first_name} ${selectedApplication.last_name} has been rejected.`, 'success');
        setShowModal(false);
        setRejectionReason('');
        fetchPendingReviews();
        setTimeout(() => setShowSuccessModal(false), 3000);
      } else {
        setMessage(data.message || 'Failed to reject application');
        setIsError(true);
        if (addNotification) addNotification(data.message || 'Failed to reject application', 'error');
      }
    } catch (error) {
      console.error('Error rejecting application:', error);
      setMessage('Network error. Please try again.');
      setIsError(true);
      if (addNotification) addNotification('Network error. Please try again.', 'error');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (application) => {
    setSelectedApplication(application);
    setRejectionReason('');
    setShowModal(true);
  };

  const viewEvidence = (evidenceUrl) => {
    if (evidenceUrl) {
      window.open(evidenceUrl, '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  return (
    <div className="admin-review-panel">
      <div className="review-container">
        <div className="review-header">
          <div className="header-icon">
            <i className="fas fa-gavel"></i>
          </div>
          <h2>Associate Member Review</h2>
          <p>Review and process associate member applications with supporting evidence</p>
        </div>

        {message && (
          <div className={`alert-message ${isError ? 'alert-error' : 'alert-success'}`}>
            <i className={`fas ${isError ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
            <span>{message}</span>
            <button className="alert-close" onClick={() => setMessage('')}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
            <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="success-icon">
                {successMessage.includes('APPROVED') ? '✅' : '❌'}
              </div>
              <h3 className={successMessage.includes('APPROVED') ? 'approved-text' : 'rejected-text'}>
                {successMessage.includes('APPROVED') ? 'Approved!' : 'Rejected!'}
              </h3>
              <p>{successMessage}</p>
              <button className="success-modal-btn" onClick={() => setShowSuccessModal(false)}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card pending">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-info">
              <span className="stat-value">{pendingReviews.length}</span>
              <span className="stat-label">Pending Review</span>
            </div>
          </div>
          <div className="stat-card approved">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-info">
              <span className="stat-value">{approvedReviews.length}</span>
              <span className="stat-label">Approved</span>
            </div>
          </div>
          <div className="stat-card rejected">
            <div className="stat-icon">
              <i className="fas fa-times-circle"></i>
            </div>
            <div className="stat-info">
              <span className="stat-value">{rejectedReviews.length}</span>
              <span className="stat-label">Rejected</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="review-tabs">
          <button 
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <i className="fas fa-clock"></i>
            Pending
            {pendingReviews.length > 0 && <span className="tab-badge">{pendingReviews.length}</span>}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            <i className="fas fa-check-circle"></i>
            Approved
            {approvedReviews.length > 0 && <span className="tab-badge approved-badge">{approvedReviews.length}</span>}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => setActiveTab('rejected')}
          >
            <i className="fas fa-times-circle"></i>
            Rejected
            {rejectedReviews.length > 0 && <span className="tab-badge rejected-badge">{rejectedReviews.length}</span>}
          </button>
        </div>

        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div className="applications-list">
            {pendingReviews.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-check-double"></i>
                <h3>No Pending Applications</h3>
                <p>All associate applications have been processed.</p>
              </div>
            ) : (
              pendingReviews.map((application) => (
                <div key={application.id} className="application-card">
                  <div className="card-header">
                    <div className="applicant-avatar">
                      <i className="fas fa-user-tie"></i>
                    </div>
                    <div className="applicant-info">
                      <h3>{application.first_name} {application.last_name}</h3>
                      <p><i className="fas fa-envelope"></i> {application.email}</p>
                      <p><i className="fas fa-phone"></i> {application.phone || 'Not provided'}</p>
                      <p><i className="fas fa-id-card"></i> {application.membership_number}</p>
                    </div>
                    <div className="request-date">
                      <i className="fas fa-calendar-alt"></i> {formatDate(application.created_at)}
                    </div>
                  </div>

                  <div className="card-details">
                    <div className="details-grid">
                      <div className="detail-item">
                        <label>Occupation</label>
                        <p>{application.occupation || 'Not provided'}</p>
                      </div>
                      <div className="detail-item">
                        <label>Address</label>
                        <p>{application.address || 'Not provided'}</p>
                      </div>
                    </div>

                    {application.evidence_file_url && (
                      <div className="evidence-section">
                        <label><i className="fas fa-file-alt"></i> Supporting Evidence</label>
                        <div className="evidence-preview">
                          {application.evidence_file_url.match(/\.(jpg|jpeg|png)$/i) ? (
                            <img 
                              src={application.evidence_file_url} 
                              alt="Evidence" 
                              className="evidence-image"
                              onClick={() => viewEvidence(application.evidence_file_url)}
                            />
                          ) : (
                            <div 
                              className="evidence-file"
                              onClick={() => viewEvidence(application.evidence_file_url)}
                            >
                              <i className="fas fa-file-pdf"></i>
                              <span>View Document</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="card-actions">
                    <button 
                      className="action-btn approve"
                      onClick={() => handleApprove(application)}
                      disabled={processingId === application.id}
                    >
                      {processingId === application.id ? (
                        <div className="btn-spinner"></div>
                      ) : (
                        <>
                          <i className="fas fa-check"></i>
                          Approve Application
                        </>
                      )}
                    </button>
                    <button 
                      className="action-btn reject"
                      onClick={() => openRejectModal(application)}
                      disabled={processingId === application.id}
                    >
                      <i className="fas fa-times"></i>
                      Reject Application
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Approved Tab */}
        {activeTab === 'approved' && (
          <div className="applications-list">
            {approvedReviews.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-check-circle"></i>
                <h3>No Approved Applications</h3>
              </div>
            ) : (
              approvedReviews.map((application) => (
                <div key={application.id} className="application-card approved-card">
                  <div className="card-header">
                    <div className="applicant-info">
                      <h3>{application.first_name} {application.last_name}</h3>
                      <p>{application.email}</p>
                    </div>
                    <div className="status-badge approved">
                      <i className="fas fa-check"></i> Approved
                    </div>
                  </div>
                  <div className="card-footer">
                    <span>Approved on: {formatDate(application.reviewed_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Rejected Tab */}
        {activeTab === 'rejected' && (
          <div className="applications-list">
            {rejectedReviews.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-times-circle"></i>
                <h3>No Rejected Applications</h3>
              </div>
            ) : (
              rejectedReviews.map((application) => (
                <div key={application.id} className="application-card rejected-card">
                  <div className="card-header">
                    <div className="applicant-info">
                      <h3>{application.first_name} {application.last_name}</h3>
                      <p>{application.email}</p>
                    </div>
                    <div className="status-badge rejected">
                      <i className="fas fa-times"></i> Rejected
                    </div>
                  </div>
                  {application.rejection_reason && (
                    <div className="rejection-reason">
                      <strong>Reason:</strong> {application.rejection_reason}
                    </div>
                  )}
                  <div className="card-footer">
                    <span>Rejected on: {formatDate(application.reviewed_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        <div className="review-footer">
          <button className="refresh-btn" onClick={fetchPendingReviews}>
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>

      {/* Rejection Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-exclamation-triangle"></i> Reject Application</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>You are about to reject the application of:</p>
              <div className="applicant-details">
                <strong>{selectedApplication?.first_name} {selectedApplication?.last_name}</strong>
                <br />
                <span>{selectedApplication?.email}</span>
              </div>
              <div className="form-group">
                <label htmlFor="rejection-reason">Reason for Rejection</label>
                <textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows="4"
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-confirm-reject" onClick={handleReject}>Confirm Rejection</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviewPanel;